/**
 * The profile menu, and the organisation / location context.
 *
 * The context switcher is not a convenience filter. In this ERP a location grant *is* the scope of what
 * someone may see, and a Major Locus grant reaches its Minor Loci. So the control here narrows the view
 * within what the server already allows — it can never widen it. Picking a location the user has no grant
 * for would simply return nothing, because RLS decides, not this component.
 *
 * That is why the list is built from `api.my_access()` grants rather than from every location in the
 * database: offering a choice the server will refuse is worse than not offering it.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Building2, Check, ChevronDown, LogOut, MapPin, Palette, ShieldCheck, User, UserCog } from 'lucide-react';
import { useSession } from '../lib/session';
import { supabase } from '../lib/supabase';
import { SectionLabel, cx } from '../ui/primitives';
import { MyAccountDrawer } from './MyAccountDrawer';

/** Closes a popover on an outside click or Escape. */
function useDismiss(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  return ref;
}

export function UserMenu({ onAppearance, appearanceLabel }: { onAppearance?: () => void; appearanceLabel?: string } = {}) {
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const { me, access, signOut } = useSession();
  const ref = useDismiss(open, () => setOpen(false));

  const name = me?.person?.display_name ?? me?.email ?? 'Not signed in';
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  const sensitive = (access?.permissions ?? []).filter((p) => p.sensitivity !== 'NORMAL').length;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="flex h-[28px] items-center gap-1.5 rounded-[var(--radius-control)] pl-1 pr-1.5 hover:bg-surface-sunken"
      >
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand text-[11px] font-semibold text-on-brand">
          {initials || '?'}
        </span>
        <ChevronDown size={12} className="text-ink-muted" aria-hidden />
      </button>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-1 w-72 overflow-hidden rounded-[var(--radius-panel)] border border-line bg-surface shadow-[var(--shadow-overlay)]"
        >
          <div className="border-b border-line px-3 py-2.5">
            <p className="truncate text-body font-medium">{name}</p>
            <p className="truncate text-small text-ink-muted">{me?.email}</p>
            {me?.status && me.status !== 'ACTIVE' ? (
              <p className="mt-1 text-small text-refused">Account status: {me.status}</p>
            ) : null}
          </div>

          <div className="border-b border-line px-3 py-2">
            <SectionLabel>Organisation</SectionLabel>
            <p className="mt-0.5 flex items-center gap-1.5 text-body">
              <Building2 size={13} className="text-ink-muted" aria-hidden />
              {me?.organization?.name ?? '—'}
            </p>
            <p className="mt-0.5 text-small text-ink-muted">
              {me?.organization?.timezone} · {me?.organization?.currency}
            </p>
          </div>

          <div className="border-b border-line px-3 py-2">
            <SectionLabel>Access</SectionLabel>
            <p className="mt-0.5 text-small text-ink-muted">
              {(access?.grants ?? []).length} role{(access?.grants ?? []).length === 1 ? '' : 's'} ·{' '}
              {(access?.permissions ?? []).length} permissions
            </p>
            {sensitive > 0 ? (
              <p className="mt-0.5 flex items-center gap-1 text-small text-ink-muted">
                <ShieldCheck size={12} aria-hidden />
                {sensitive} sensitive or critical — every use is logged
              </p>
            ) : null}
            <ul className="mt-1 space-y-0.5">
              {(access?.grants ?? []).slice(0, 4).map((grant) => (
                <li key={grant.role} className="truncate text-small">
                  {grant.role_name}
                  {grant.location_ids.length > 0 ? (
                    <span className="text-ink-faint"> · {grant.location_ids.length} location(s)</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setAccountOpen(true);
            }}
            className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-body hover:bg-surface-sunken"
          >
            <UserCog size={13} className="text-ink-muted" aria-hidden />
            My account
          </button>

          {onAppearance ? (
            <button type="button" role="menuitem" onClick={() => { setOpen(false); onAppearance(); }}
              className="flex w-full items-center gap-2 border-b border-line px-3 py-2 text-left text-body hover:bg-surface-sunken">
              <Palette size={13} className="text-ink-muted" aria-hidden />
              Appearance{appearanceLabel ? <span className="ml-auto text-small text-ink-faint">{appearanceLabel}</span> : null}
            </button>) : null}

          <button
            type="button"
            role="menuitem"
            onClick={() => void signOut()}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-body hover:bg-surface-sunken"
          >
            <LogOut size={13} className="text-ink-muted" aria-hidden />
            Sign out
          </button>
        </div>
      ) : null}

      <MyAccountDrawer open={accountOpen} onClose={() => setAccountOpen(false)} />
    </div>
  );
}

export interface LocationScope {
  id: string;
  code: string;
  name: string;
  kind: string;
}

/**
 * Narrows the view to one location the user already has a grant for.
 *
 * Kept in `sessionStorage` rather than the URL for now: it is a view preference, and putting it in the
 * URL would imply a shareable, authoritative scope that the recipient may not be permitted to see.
 */
export function useLocationScope() {
  const [scopeId, setScopeId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem('rtcom.scope');
    } catch {
      return null;
    }
  });

  const set = (id: string | null) => {
    setScopeId(id);
    try {
      if (id) sessionStorage.setItem('rtcom.scope', id);
      else sessionStorage.removeItem('rtcom.scope');
    } catch {
      // Blocked storage only costs the reader their choice on the next page load.
    }
  };

  return { scopeId, setScope: set };
}

export function LocationContext() {
  const [open, setOpen] = useState(false);
  const { can } = useSession();
  const { scopeId, setScope } = useLocationScope();
  const ref = useDismiss(open, () => setOpen(false));

  const locations = useQuery({
    queryKey: ['scope-locations'],
    enabled: can('farm.locus.view') || can('plant.view') || can('masterdata.locations.manage'),
    staleTime: 300_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('id,code,name,kind')
        .eq('status', 'ACTIVE')
        .order('code')
        .limit(200);
      if (error) throw error;
      return data as LocationScope[];
    },
  });

  // RLS already returns only what the caller may see, so an empty list means "no location scope applies"
  // rather than "loading failed".
  const rows = locations.data ?? [];
  if (locations.isError || rows.length === 0) return null;

  const active = rows.find((l) => l.id === scopeId);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cx(
          'inline-flex h-[28px] max-w-[16rem] items-center gap-1.5 rounded-[var(--radius-control)] border px-2 text-small',
          active ? 'border-brand bg-brand-soft text-brand' : 'border-line-strong bg-surface text-ink-muted hover:bg-surface-sunken',
        )}
        title={active ? `Scoped to ${active.name}` : 'All locations you can see'}
      >
        <MapPin size={13} aria-hidden />
        <span className="truncate">{active ? active.code : 'All locations'}</span>
        <ChevronDown size={12} aria-hidden />
      </button>

      {open ? (
        <div className="absolute right-0 z-40 mt-1 w-80 overflow-hidden rounded-[var(--radius-panel)] border border-line bg-surface shadow-[var(--shadow-overlay)]">
          <div className="border-b border-line px-3 py-2">
            <SectionLabel>Location scope</SectionLabel>
            <p className="mt-0.5 text-small text-ink-muted">
              Narrows what you see. It cannot widen it — your grants decide that, on the server.
            </p>
          </div>

          <ul className="max-h-72 overflow-y-auto py-1" role="listbox">
            <li>
              <button
                type="button"
                onClick={() => {
                  setScope(null);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-body hover:bg-surface-sunken"
              >
                <span className="w-3.5">{!scopeId ? <Check size={13} className="text-brand" /> : null}</span>
                All locations
              </button>
            </li>
            {rows.map((location) => (
              <li key={location.id}>
                <button
                  type="button"
                  onClick={() => {
                    setScope(location.id);
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-surface-sunken"
                >
                  <span className="w-3.5">
                    {scopeId === location.id ? <Check size={13} className="text-brand" /> : null}
                  </span>
                  <span className="code-text shrink-0 text-ink-muted">{location.code}</span>
                  <span className="min-w-0 flex-1 truncate text-body">{location.name}</span>
                  <span className="label-text shrink-0">{location.kind.replace('_', ' ')}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export { User };
